import React from 'react'
import propTypes from 'prop-types'
import {get, merge} from 'lodash'
import Checkbox from '@material-ui/core/Checkbox'
import theme from 'theme/index'
import themeVars from 'theme/themeVars'
import CompanyImage from 'containers/companyImage'
import FinancialString from 'containers/financialString'
import Icon from 'components/icon'
import Contacts from 'helpers/contacts'
import Dates from 'helpers/dates'
import Quotes from 'helpers/quotes'
import InvoiceHelper from 'helpers/invoices'
import {getColorForPurchaseOrderStatus, getPurchaseOrderStatusName} from 'helpers/purchasing/purchaseOrders'
import {
  getActivityIconNameForType,
  getActivityPriorityTitle,
  getActivityStatusColorForStatus,
  getAttendeeNames,
  getDefaultActivityDescForType,
} from 'helpers/activities'
import './entityObject.scss'

const getStyles = (props) => {
  const propStyles = props.styles || {}
  const hasExtraDetails = (props.rightSubTitle || props.rightBodyText)

  const entityWrapper = {
    display: 'flex',
    justifyContent: 'flex-start',
    width: 'initial',
    minHeight: 60,
    cursor: 'pointer',
    fontSize: '13px',
    borderBottom: props.showBorder === false ? null : `1px solid ${themeVars.palette.borderColor}`,
  }

  const styles = {
    entityWrapper,
    entityStatus: {
      width: 5,
      height: 'auto',
      backgroundColor: props.status,
      marginRight: 4,
    },
    entityContent: {
      display: 'flex',
      width: '100%',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: 6,
    },
    entityLeftContent: {},
    entityDetails: {
      width: (hasExtraDetails) ? '70%' : '100%',
      textAlign: 'left',
      marginLeft: 10,
      lineHeight: '20px',
    },
    entityTitle: {},
    entitySubtitle: {},
    entityBodyText: {},
    entityExtraDetails: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '30%',
      textAlign: 'right',
      paddingRight: 12,
    },
    entityRightSubtitle: {
      marginBottom: 20,
    },
    entityRightBodyText: {},
    entityExpectedDateWrapper: {
      display: 'flex',
      justifyContent: 'flex-end',
      color: themeVars.palette.lightTextColor,
    },
    entityExpectedDateDescription: {
      fontWeight: 600,
    },
    entityExpectedDate: {
      fontWeight: 'normal',
      marginLeft: 2,
    },
    title: {
      color: themeVars.palette.darkTextColor,
      fontWeight: 600,
    },
    subtitle: {
      color: themeVars.palette.primaryFontColor,
    },
    bodyText: {
      color: themeVars.palette.primaryFontColor,
    },
    highlightedText: {
      color: themeVars.palette.accent1Color,
    },
    boldText: {
      fontWeight: 500,
    },
    icon: {
      color: themeVars.palette.primaryIconColor,
      selected: themeVars.palette.primary1Color,
    },
    checkbox: {},
  }

  return {...styles, ...propStyles}
}

const getDarkStyles = (props) => {
  const {selected} = props
  const style = getStyles(props)

  const fontColor = selected
    ? themeVars.palette.contrastActiveTextColor
    : themeVars.palette.contrastTextColor
  const titleColor = selected
    ? themeVars.palette.contrastActiveTextColor
    : themeVars.palette.contrastTextColor

  const change = {
    entityWrapper: {
      borderBottom: '1px solid ' + themeVars.palette.contrastBorderColor,
    },
    title: {
      color: titleColor,
    },
    subtitle: {
      color: fontColor,
    },
    bodyText: {
      color: fontColor,
    },
    icon: {
      color: fontColor,
    },
  }
  return merge(style, change)
}

const EntityObject = (props) => {
  const {
    leftContent,
    iconName,
    iconSize,
    title,
    subtitle,
    bodyText,
    rightSubTitle,
    rightBodyText,
    hasExtraDetails,
    selected,
    onClick,
    dark = false,
    showCheck = false,
    checked = false,
    onCheck,
  } = props

  const styles = props.style || getStyles(props)
  const entityStatus = props.hasStatus
    ? <div className="entityStatus" style={styles.entityStatus}/>
    : ''

  let extraDetails
  if (rightSubTitle || rightBodyText) {
    if (hasExtraDetails === true) {
      extraDetails = (
        <div className="entityExtraDetails" style={styles.entityExtraDetails}>
          <div className="entityRightSubtitle" style={styles.entityRightSubtitle}>
            {props.rightSubTitle}
          </div>
          <div className="entityRightBodyText" style={styles.entityRightBodyText}>
            {props.rightBodyText}
          </div>
        </div>
      )
    }
  }

  const checkbox = showCheck
    ? (
      <div className="entityCheckbox" style={styles.checkbox} onClick={e => e.stopPropagation()}>
        <Checkbox checked={checked} onChange={onCheck}/>
      </div>
    )
    : null

  let className = 'entityWrapper'
  if (selected) className += ' selected'
  if (dark) className += ' dark'

  const leftElement = leftContent
    ? leftContent
    : (
      <Icon
        color={selected ? styles.icon.selected : styles.icon.color}
        name={iconName}
        size={iconSize}
      />
    )

  return (
    <div className={className} style={styles.entityWrapper} onClick={onClick}>
      {entityStatus}
      <div className="entityContent" style={styles.entityContent}>
        <div className="entityLeftContent" style={styles.entityLeftContent}>
          {leftElement}
        </div>
        <div className="entityDetails" style={styles.entityDetails}>
          <div className="entityTitle" style={styles.entityTitle}>
            {title}
          </div>
          <div className="entitySubtitle" style={styles.entitySubtitle}>
            {subtitle}
          </div>
          <div className="entityBodyText" style={styles.entityBodyText}>
            {bodyText}
          </div>
        </div>
        {extraDetails}
        {checkbox}
      </div>
    </div>
  )
}

EntityObject.propTypes = {
  iconName: propTypes.string,
  iconSize: propTypes.number,
  title: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
  ]).isRequired,
  subtitle: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
  ]),
  bodyText: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
  ]),
  rightSubTitle: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
  ]),
  rightBodyText: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
  ]),
  leftContent: propTypes.any,
  hasStatus: propTypes.bool,
  hasExtraDetails: propTypes.bool,
  dark: propTypes.bool,
  selected: propTypes.bool,
  showCheck: propTypes.bool,
  checked: propTypes.bool,
  style: propTypes.object,
  onClick: propTypes.func,
  onCheck: propTypes.func,
}

// ---------------

const ContactEntity = (props) => {
  const {entity, dark = false, selected = false, ...otherProps} = props

  let styles
  if (dark) {
    styles = getDarkStyles(props)
  } else {
    styles = getStyles(props)
  }

  const title = (
    <div style={styles.title}>
      {entity.forename} {entity.surname}
    </div>
  )
  const companyTypeId = get(entity, 'company.type.id', -1)

  const subtitle = (companyTypeId !== -1)
    ? <div style={styles.subtitle}>{entity.company.name}</div>
    : <div style={styles.subtitle}>{entity.email}</div>

  const bodyText = (companyTypeId !== -1)
    ? <div style={styles.bodyText}>{entity.email}</div>
    : <div style={styles.bodyText}/>

  const mainContactStyle = (entity.type.id === 1)
    ? {...styles.rightSubTitle, ...styles.highlightedText}
    : styles.rightSubTitle

  const rightSubTitle = (
    <div style={mainContactStyle}>
      {Contacts.getContactTypeTitleForType(entity.type.id)}
    </div>
  )

  return (
    <EntityObject
      title={title}
      subtitle={subtitle}
      bodyText={bodyText}
      rightSubTitle={rightSubTitle}
      rightBodyText={''}
      iconName={theme.icons.contact}
      iconSize={18}
      onClick={props.onClick}
      hasExtraDetails={props.hasExtraDetails}
      selected={selected}
      styles={styles}
      dark={dark}
      {...otherProps}
    />
  )
}

ContactEntity.propTypes = {
  entity: propTypes.shape({
    id: propTypes.number.isRequired,
    companyId: propTypes.number.isRequired,
    forename: propTypes.string.isRequired,
    surname: propTypes.string.isRequired,
    email: propTypes.string.isRequired,
    companyType: propTypes.oneOfType([
      propTypes.shape({
        id: propTypes.number.isRequired,
      }),
      propTypes.number,
    ]).isRequired,
    type: propTypes.oneOfType([
      propTypes.shape({
        id: propTypes.number.isRequired,
      }),
      propTypes.number,
    ]).isRequired,
    company: propTypes.shape({
      name: propTypes.string.isRequired,
    }),
  }).isRequired,
  selected: propTypes.bool,
  onClick: propTypes.func,
  hasExtraDetails: propTypes.bool,
  dark: propTypes.bool,
}

// -------------

const CompanyEntity = (props) => {
  const {entity, dark, selected, ...otherProps} = props

  let styles
  if (dark) {
    styles = getDarkStyles(props)
  } else {
    styles = getStyles(props)
  }

  const title = <div style={styles.title}>{entity.name}</div>

  let subtitle = ''
  if (entity.maincontact && entity.maincontact.length > 0) {
    const mainContact = entity.maincontact[0]
    subtitle = (
      <div style={styles.subtitle}>
        {`${mainContact.forename} ${mainContact.surname}`}
      </div>
    )
  }

  const leftContent = (
    <CompanyImage
      company={entity}
      companyType={entity.type.id}
      size={18}
    />
  )

  return (
    <EntityObject
      title={title}
      subtitle={subtitle}
      leftContent={leftContent}
      iconName={theme.icons.company}
      iconSize={18}
      onClick={props.onClick}
      selected={selected}
      styles={styles}
      dark={dark}
      {...otherProps}
    />
  )
}

CompanyEntity.propTypes = {
  entity: propTypes.object,
  selected: propTypes.bool,
  dark: propTypes.bool,
  onClick: propTypes.func,
}

// ---------------

const ActivityEntity = (props) => {
  const styles = getStyles(props)
  const {entity, selected, selectedLabel} = props
  let {hasExtraDetails = false} = props

  let label = entity.desc
  if (entity.threadRef) {
    label = entity.threadDesc
      ? `[${entity.threadRef}: ${entity.threadDesc}]: ${label}`
      : `[${entity.threadRef}]: ${label}`
  }

  const activityBaseType = entity.typeId
  const iconName = getActivityIconNameForType(activityBaseType)

  const title = (entity.desc === '')
    ? <div style={styles.title}>{getDefaultActivityDescForType(activityBaseType)}</div>
    : <div style={styles.title}>{label}</div>

  const subtitle = (
    <div style={styles.subtitle}>
      {Dates.getDateTimeRangeString(entity.allDay, entity.startDate, entity.endDate)}
    </div>
  )

  let bodyText = ''
  if (entity.attendees && entity.attendees.length !== 0) {
    const attendeeNames = getAttendeeNames(entity.attendees)
    bodyText = <div style={styles.subtitle}>{attendeeNames}</div>
  } else if (entity.contact) {
    bodyText = <div style={styles.subtitle}>{entity.contact.forename + ' ' + entity.contact.surname}</div>
  } else if (entity.company) {
    bodyText = <div style={styles.subtitle}>{entity.company.name}</div>
  }

  let rightSubTitle = ''

  if (!entity.deadline) {
    rightSubTitle = (
      <div
        className="entityExpectedDateWrapper"
        style={styles.entityExpectedDateWrapper}>
        <div
          className="entityExpectedDateDescription"
          style={styles.entityExpectedDateDescription}>
          Deadline -
        </div>
        <div
          className="entityExpectedDate"
          style={styles.entityExpectedDate}>
          {Dates.getDateString(entity.deadline)}
        </div>
      </div>
    )
  }


  let rightBodyText = (
    <div style={styles.subtitle}>
      {getActivityPriorityTitle(entity.priority.id)}
    </div>
  )

  if (!hasExtraDetails && selected && selectedLabel && selectedLabel.length > 0) {
    rightSubTitle = (
      <div className={'entitySelectedLabel'}>
        {selectedLabel}
      </div>
    )
    hasExtraDetails = true
    rightBodyText = ''
  }
  return (
    <EntityObject
      title={title}
      subtitle={subtitle}
      bodyText={bodyText}
      rightSubTitle={rightSubTitle}
      rightBodyText={rightBodyText}
      iconName={iconName}
      iconSize={18}
      hasStatus={true}
      status={getActivityStatusColorForStatus(entity.status)}
      onClick={props.onClick}
      hasExtraDetails={hasExtraDetails}
      selected={selected}
    />
  )
}

ActivityEntity.propTypes = {
  entity: propTypes.object,
  selectedLabel: propTypes.string,
  selected: propTypes.bool,
  hasExtraDetails: propTypes.bool,
  onClick: propTypes.func,
}

// ---------------

const OpportunityEntity = (props) => {
  const styles = getStyles(props)
  const {entity} = props

  const title = <div style={styles.title}>{entity.ref} - {entity.desc}</div>
  const subtitle = (
    <div style={{...styles.subtitle, ...styles.highlightedText}}>
      <FinancialString value={entity.value}/>
    </div>
  )
  const companyName = (entity.company) ? entity.company.name : ''
  const bodyText = <div style={styles.bodyText}>{companyName}</div>

  let rightSubTitle
  if (entity.expectedDate) {
    rightSubTitle = (
      <div className="entityExpectedDateWrapper" style={styles.entityExpectedDateWrapper}>
        <div className="entityExpectedDateDescription" style={styles.entityExpectedDateDescription}>
          Expected On -
        </div>
        <div className="entityExpectedDate" style={styles.entityExpectedDate}>
          {Dates.getDateString(entity.expectedDate)}
        </div>
      </div>
    )
  }

  const stageName = (entity.stage) ? entity.stage.name : ''
  const probabilityEstimate = entity.probability + '% ' + stageName
  const rightBodyText = <div style={styles.bodyText}>{probabilityEstimate}</div>

  return (
    <EntityObject
      title={title}
      subtitle={subtitle}
      bodyText={bodyText}
      rightSubTitle={rightSubTitle}
      rightBodyText={rightBodyText}
      iconName={theme.icons.opportunity}
      iconSize={18}
      onClick={props.onClick}
      hasExtraDetails={entity.hasExtraDetails}
    />
  )
}

OpportunityEntity.propTypes = {
  entity: propTypes.object.isRequired,
  onClick: propTypes.func,
}

// ---------------

const QuoteEntity = (props) => {
  const styles = getStyles(props)
  const {entity} = props

  let companyName = ''
  if (entity.company) {
    if (entity.company.type && entity.company.type.id === Contacts.COMPANY_TYPE.individual) {
      if (entity.contact) {
        companyName = `${entity.contact.forename} ${entity.contact.surname}`
      }
    } else {
      companyName = entity.company.name
    }
  } else if (
    entity.contact &&
    entity.contact.companyType &&
    entity.contact.companyType.id === Contacts.COMPANY_TYPE.individual
  ) {
    companyName = `${entity.contact.forename} ${entity.contact.surname}`
  }

  const title = (
    <div style={styles.title}>
      {entity.ref}/{entity.revision} - {entity.desc}
    </div>
  )
  const subtitle = (
    <div style={{...styles.subtitle, ...styles.highlightedText}}>
      <FinancialString
        value={entity.totalPrice}
        currencyId={entity.currencyId}/>
    </div>
  )
  const bodyText = <div style={styles.bodyText}>{companyName}</div>

  let rightSubTitle
  if (entity.hasExtraDetails === true) {
    rightSubTitle = (entity.expectedDate) ? (
      <div className="entityExpectedDateWrapper" style={styles.entityExpectedDateWrapper}>
        <div className="entityExpectedDateDescription" style={styles.entityExpectedDateDescription}>
          Expected On -
        </div>
        <div className="entityExpectedDate" style={styles.entityExpectedDate}>
          {Dates.getDateString(entity.expectedDate)}
        </div>
      </div>
    ) : ''
  }

  const rightBodyText = <div style={styles.bodyText}>{entity.status.label}</div>

  return (
    <EntityObject
      title={title}
      subtitle={subtitle}
      bodyText={bodyText}
      rightSubTitle={rightSubTitle}
      rightBodyText={rightBodyText}
      iconName={theme.icons.order}
      iconSize={18}
      onClick={props.onClick}
      hasExtraDetails={entity.hasExtraDetails}
      hasStatus={true}
      status={Quotes.getColorForQuoteStatus(theme, entity.status.value)}
    />
  )
}

QuoteEntity.propTypes = {
  entity: propTypes.object.isRequired,
  onClick: propTypes.func,
}

// ---------------

const PurchaseOrderEntity = (props) => {
  const styles = getStyles(props)
  const {entity} = props

  const companyName = get(entity, 'company.name', null)
  const title = <div style={styles.title}>{entity.reference} - {entity.description}</div>
  const subtitle = (
    <div style={{...styles.subtitle, ...styles.highlightedText}}>
      <FinancialString value={entity.totalCost} currencyId={entity.currencyId}/>
    </div>
  )
  const bodyText = <div style={styles.bodyText}>{companyName}</div>
  const rightSubTitle = null
  const rightBodyText = (
    <div style={styles.bodyText}>
      {getPurchaseOrderStatusName(entity.status)}
    </div>
  )

  return (
    <EntityObject
      title={title}
      subtitle={subtitle}
      bodyText={bodyText}
      rightSubTitle={rightSubTitle}
      rightBodyText={rightBodyText}
      iconName={theme.icons.order}
      iconSize={18}
      onClick={props.onClick}
      hasExtraDetails={entity.hasExtraDetails}
      hasStatus={true}
      status={getColorForPurchaseOrderStatus(entity.status)}
    />
  )
}

PurchaseOrderEntity.propTypes = {
  entity: propTypes.object.isRequired,
  onClick: propTypes.func,
}

// ---------------

const InvoiceEntity = (props) => {
  const styles = getStyles(props)
  const {entity, currencyId} = props

  const title = <div style={styles.title}>{entity.ref} - {entity.desc}</div>
  const totalPrice = (entity.order) ? entity.order.totalPrice : 0

  const subtitle = (
    <div style={{...styles.subtitle, ...styles.highlightedText}}>
      <FinancialString value={totalPrice} currencyId={currencyId}/>
    </div>
  )
  const companyName = (entity.company) ? entity.company.name : ''
  const bodyText = <div style={styles.bodyText}>{companyName}</div>
  let rightSubTitle
  if (entity.hasExtraDetails === true) {
    const creditNoteStatus = (entity.creditNote) ? 'Credited On - ' : 'Invoiced On - '
    rightSubTitle = (
      <div className="entityExpectedDateWrapper" style={styles.entityExpectedDateWrapper}>
        <div className="entityExpectedDateDescription" style={styles.entityExpectedDateDescription}>
          {creditNoteStatus}
        </div>
        <div className="entityExpectedDate" style={styles.entityExpectedDate}>
          {Dates.getDateString(entity.invoiceDate)}
        </div>
      </div>
    )
  }
  const rightBodyText = <div style={styles.bodyText}/>
  const iconName = (entity.creditNote) ? 'credit_note' : theme.icons.invoice
  return (
    <EntityObject
      title={title}
      subtitle={subtitle}
      bodyText={bodyText}
      rightSubTitle={rightSubTitle}
      rightBodyText={rightBodyText}
      iconName={iconName}
      iconSize={18}
      onClick={props.onClick}
      hasExtraDetails={entity.hasExtraDetails}
      hasStatus={true}
      status={InvoiceHelper.getColorBasedOnCreditNote(theme, entity.creditNote)}
    />
  )
}

InvoiceEntity.propTypes = {
  entity: propTypes.object.isRequired,
  currencyId: propTypes.number,
  onClick: propTypes.func,
}

// ---------------

const QuoteAmendmentEntity = (props) => {
  const styles = getStyles(props)
  const {entity, currencyId} = props

  let subtitle, iconName
  const title = <div style={styles.title}>{entity.type.label}</div>
  subtitle = <div style={{...styles.subtitle, ...styles.highlightedText}}>{entity.desc}</div>
  const bodyText = <div style={styles.bodyText}>{entity.creatorName}</div>

  switch (entity.type.id) {
    case 1: // item added
      iconName = 'add_property'
      subtitle = (
        <div style={{...styles.subtitle, ...styles.highlightedText}}>
          {entity.desc} - x{entity.itemQuantity} {entity.itemPartCode} @ {<FinancialString
          value={entity.itemUnitPrice} currencyId={currencyId}/>} each
        </div>
      )
      break
    case 0: // Totals updated
    case 2: // item edit
    case 4: // description updated
    case 6: // item added
      iconName = 'add_property'
      break
    case 3: // item deleted
    case 5: // item dormant
      iconName = 'remove_property'
      break
  }
  const rightBodyText = (
    <div style={styles.bodyText}>
      {get(entity, ['contact', 'fullName'])}
    </div>
  )

  return (
    <EntityObject
      title={title}
      subtitle={subtitle}
      bodyText={bodyText}
      rightSubTitle={"Test"}
      rightBodyText={rightBodyText}
      iconName={iconName}
      iconSize={18}
      onClick={props.onClick}
      hasExtraDetails={true}
    />
  )
}

QuoteAmendmentEntity.propTypes = {
  entity: propTypes.object,
  currencyId: propTypes.number,
  onClick: propTypes.func,
}


export default EntityObject

export {
  ContactEntity,
  CompanyEntity,
  ActivityEntity,
  OpportunityEntity,
  QuoteEntity,
  PurchaseOrderEntity,
  QuoteAmendmentEntity,
  InvoiceEntity,
}
